import type { CurrentUserStore as CurrentUserStoreInterface } from '@front-app/store/modules/currentUser';
import type { StoreCache } from '@front-app/store/util/cache';
import type {
	SurveyAnswersResponse,
	SurveyResponse,
	UpsertSurveyAnswersRequest,
	UpsertSurveyRequest,
} from '@front-app/api-generated/learning-management-api';
import type { Ref } from '@vue/composition-api';
import { watch } from '@vue/composition-api';
import { STARTABLE_STATUSES, useCache } from '@front-app/store/util/cache';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { STATUS } from '@front-app/constants/state';
import { surveysApi } from '../api/surveys-api';

export interface SurveysStore {
	// Group level
	getGroupSurveys(groupId: string): SurveyResponse[];
	getGroupSurveysStatus(groupId: string): Ref<STATUS>;
	fetchSurveysForGroup(groupId: string, force?: boolean): Promise<void>;
	createSurveyForGroup(groupId: string, request: UpsertSurveyRequest): Promise<string>;
	updateSurveyForGroup(groupId: string, surveyId: string, request: UpsertSurveyRequest): Promise<void>;
	// Survey level
	getSurvey(surveyId: string): SurveyResponse;
	getSurveyStatus(surveyId: string): Ref<STATUS>;
	fetchSurvey(surveyId: string, force?: boolean): Promise<void>;
	fetchSurveyResponses(surveyId: string): Promise<SurveyAnswersResponse[]>;
	answerSurvey(surveyId: string, request: UpsertSurveyAnswersRequest): Promise<void>;
	deleteSurvey(surveyId: string): void;
}

export const createSurveysStore = (
	currentUserStore: CurrentUserStoreInterface,
	groupSurveysCache: StoreCache<string, string[]>,
	surveysCache: StoreCache<string, SurveyResponse>,
): SurveysStore => {
	const groupSurveys = useCache(groupSurveysCache, () => []);
	const surveys = useCache(surveysCache, getEmptySurvey);

	watch(() => currentUserStore.isLoggedIn, (isLoggedIn) => {
		if (!isLoggedIn) {
			groupSurveys.clear();
			surveys.clear();
		}
	}, { immediate: true });

	// Group level
	const getGroupSurveys = (groupId: string) => groupSurveys.get(groupId).map(surveys.get);
	const fetchSurveysForGroup = async (groupId: string, force = false): Promise<void> => {
		if (force || STARTABLE_STATUSES.includes(groupSurveys.getStatus(groupId).value)) {
			const { track } = useAsyncStatus(groupSurveys.getStatus(groupId));
			const { data } = await track(surveysApi.listSurveysForGroup(groupId));
			data.forEach((survey) => surveys.set(survey.id, survey));
			groupSurveys.set(groupId, data.map(({ id }) => id));
		}
	};
	const createSurveyForGroup = async (groupId: string, request: UpsertSurveyRequest): Promise<string> => {
		const { data } = await surveysApi.upsertSurveyForGroup(groupId, request);
		surveys.setWithStatus(data.id, data, STATUS.SUCCESS);
		groupSurveys.set(groupId, [data.id, ...groupSurveys.get(groupId)]);
		return data.id;
	};
	const updateSurveyForGroup = async (groupId: string, surveyId: string, request: UpsertSurveyRequest): Promise<void> => {
		const { track } = useAsyncStatus(surveys.getStatus(surveyId));
		surveys.update(surveyId, { ...request, dateModified: new Date().toISOString() });
		await track(surveysApi.upsertSurveyForGroup(groupId, { ...request, id: surveyId }));
	};

	// Survey level
	const fetchSurvey = async (surveyId: string, force = false): Promise<void> => {
		if (force || STARTABLE_STATUSES.includes(surveys.getStatus(surveyId).value)) {
			const { track } = useAsyncStatus(surveys.getStatus(surveyId));
			const { data } = await track(surveysApi.getSurveyById(surveyId));
			surveys.update(data.id, data);
		}
	};
	const answerSurvey = async (surveyId: string, request: UpsertSurveyAnswersRequest): Promise<void> => {
		const { data } = await surveysApi.upsertSurveyResponseForSurvey(surveyId, request);
		surveys.update(surveyId, { userResponse: data });
	};
	const deleteSurvey = async (surveyId: string): Promise<void> => {
		groupSurveys.keys()
			.forEach((groupId: string) => {
				const surveyIds = groupSurveys.get(groupId);
				groupSurveys.set(groupId, surveyIds.filter((existingId) => existingId !== surveyId));
			});
		await surveysApi.deleteSurveyById(surveyId);
	};
	const fetchSurveyResponses = async (surveyId: string): Promise<SurveyAnswersResponse[]> => {
		const results = await surveysApi.listSurveyResponsesForSurvey(surveyId);
		return results.data;
	};

	return {
		getGroupSurveys,
		getGroupSurveysStatus: groupSurveys.getStatus,
		fetchSurveysForGroup,
		createSurveyForGroup,
		updateSurveyForGroup,
		getSurvey: surveys.get,
		getSurveyStatus: surveys.getStatus,
		fetchSurvey,
		fetchSurveyResponses,
		answerSurvey,
		deleteSurvey,
	};
};

const globalGroupSurveysCache = {};
const globalSurveysCache = {};
export const getSurveysStore = (): SurveysStore => createSurveysStore(
	CurrentUserStore,
	globalGroupSurveysCache,
	globalSurveysCache,
);

function getEmptySurvey(): SurveyResponse {
	return {
		id: '',
		surveyContent: '',
		dateCreated: '',
		dateModified: '',
		userResponse: undefined,
	};
}
